"use strict";
/* globals module */
/**
 * @module filesClient
 * @description  Files Client provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Files Route Definition to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var FilesClient = /** @class */ (function () {
    function FilesClient(filesRoute, apiClient, filesStreamsClient, filesBatchClient, filesACLClient) {
        this.filesRoute = filesRoute;
        this.apiClient = apiClient;
        this.filesStreamsClient = filesStreamsClient;
        this.filesBatchClient = filesBatchClient;
        this.filesACLClient = filesACLClient;
    }
    Object.defineProperty(FilesClient.prototype, "routeDefinition", {
        get: function () {
            return this.filesRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FilesClient.prototype, "streams", {
        get: function () {
            return this.filesStreamsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FilesClient.prototype, "batch", {
        get: function () {
            return this.filesBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FilesClient.prototype, "acl", {
        get: function () {
            return this.filesACLClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of file resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example filesClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    FilesClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns requested file resource.
     * @method
     * @param id File id which uniquely identifies file resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example filesClient.get('<file-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    FilesClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.filesRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the update file action has been performed; this action will update a file resource if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `filesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(fileEntry);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A file entry object used to update specific file entry resource in the system.
     * @example // fileEntry is a file resource previously fetched using get action.
                    fileEntry.description = '<description>';
                    filesClient.update(fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    FilesClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unlink action has been performed. This action will remove one or many file resources from the system if successfully completed. If derived resource's format is passed, such as `width` and `height` for the image type of file resource, the operation will remove just derived resource. Otherwise, specified file and all its accompanying derived resources will be removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicFilesRouteService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(fileEntry);
     * let uri = params['model'].links('unlink').href;
     * ```
     * @method
     * @param data
     * @param options
     * @returns A promise that is resolved once the unlink action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action. The following action will remove the original file resource and all accompanying derived file resources.
                    filesClient.unlink(fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
                // fileEntry is a file resource previously fetched using get action. The following action will remove derived file resource only.
                    filesClient.unlink(fileEntry, {width: <width>, height: <height>})
                        .then(function (data) {
                            // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    FilesClient.prototype.unlink = function (data, options) {
        return this.apiClient.delete(this.filesRoute.unlink(data, options));
    };
    /**
     * Returns a promise that is resolved once the link action has been performed; this action links file resource from other modules into the Files module (For example: file resources from the Media Vault module can be linked directly into the Files module).
     * @method
     * @param data A file object that need to be inserted into the system.
     * @returns A promise that is resolved once the link action has been performed.
     * @example filesClient.link(fileObject)
                   .then(function (data) {
                       // perform success action here
                   },
                    function (response, status, headers, config) {
                        // perform error handling here
                   });
    **/
    FilesClient.prototype.link = function (data) {
        return this.apiClient.post(this.routeDefinition.link(), this.routeDefinition.createParams(data));
    };
    var _a, _b, _c, _d, _e;
    FilesClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.FilesRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.FilesStreamsClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.FilesBatchClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.FilesACLClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.FilesRoute !== "undefined" && _1.FilesRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object, typeof (_c = typeof _1.FilesStreamsClient !== "undefined" && _1.FilesStreamsClient) === "function" ? _c : Object, typeof (_d = typeof _1.FilesBatchClient !== "undefined" && _1.FilesBatchClient) === "function" ? _d : Object, typeof (_e = typeof _1.FilesACLClient !== "undefined" && _1.FilesACLClient) === "function" ? _e : Object])
    ], FilesClient);
    return FilesClient;
}());
exports.FilesClient = FilesClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
    */ 
